import React from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { ApolloError, useMutation } from "@apollo/client";
import { FiUploadCloud } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { IoClose } from "react-icons/io5";
import { GenerateUserModel3DFromImageDocument } from "../../gql/userModel3D";
import { floorplannerStore } from "../../store/floorplannerStore";
import { userStore } from "../../store/userStore";
import { BONI_CHECKBOX_CLASSNAME } from "../../constants/brandColors";
import { UserModel3DLibraryItem } from "../../features/floorplan3d/userModels/userModel3DTypes";
import {
  emptyUserModel3DCommerceForm,
  parseCommerceFormForCreate,
  UserModel3DCommerceFormState,
} from "../../features/floorplan3d/userModels/userModel3DCommerce";
import { UserModel3DCommerceFields } from "./UserModel3DCommerceFields";

type Props = {
  onClose: () => void;
  onGenerated: (model: UserModel3DLibraryItem) => void;
};

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const GENERATION_CREDIT_COST = 5;

const imageBaseName = (fileName: string) =>
  fileName.replace(/\.(jpe?g|png|webp)$/i, "").trim() || "3D object";

const getErrorCode = (error: unknown) =>
  error instanceof ApolloError
    ? error.graphQLErrors.find((item) => item.extensions?.code)?.extensions
        ?.code
    : undefined;

export const CreateModel3DFromImageModal: React.FC<Props> = ({
  onClose,
  onGenerated,
}) => {
  const { t } = useTranslation();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const onCloseRef = React.useRef(onClose);
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState("");
  const [shareWithCommunity, setShareWithCommunity] = React.useState(true);
  const [commerceForm, setCommerceForm] =
    React.useState<UserModel3DCommerceFormState>(() =>
      emptyUserModel3DCommerceForm(floorplannerStore.currency),
    );
  const [dragging, setDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [serverOutOfCredits, setServerOutOfCredits] = React.useState(false);
  const [generate, generation] = useMutation(
    GenerateUserModel3DFromImageDocument,
  );
  const plan = userStore.entitlements?.plan?.toUpperCase() ?? "FREE";
  const credits = userStore.entitlements?.aiCredits;
  const canChoosePrivacy = [
    "PRO",
    "PRO_YEAR",
    "ENTERPRISE",
    "DEVELOPER",
  ].includes(plan);
  const outOfCredits =
    serverOutOfCredits ||
    Boolean(
      credits &&
        !credits.unlimited &&
        (credits.remaining ?? 0) < GENERATION_CREDIT_COST,
    );
  const canBuyCredits = plan !== "ENTERPRISE";

  const previewUrl = React.useMemo(
    () => (file ? URL.createObjectURL(file) : undefined),
    [file],
  );

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  React.useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  React.useEffect(() => {
    const previousActive = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown, true);
      previousActive?.focus();
    };
  }, []);

  const chooseImage = (nextFile?: File) => {
    setError(null);
    if (!nextFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(nextFile.type)) {
      setError(t("createModel3d.chooseImageError"));
      return;
    }
    if (nextFile.size > MAX_IMAGE_BYTES) {
      setError(t("createModel3d.imageTooLarge"));
      return;
    }
    setFile(nextFile);
    setName(imageBaseName(nextFile.name));
    setShareWithCommunity(true);
    setCommerceForm(emptyUserModel3DCommerceForm(floorplannerStore.currency));
  };

  const submit = async () => {
    if (!file || !name.trim() || outOfCredits) return;
    setError(null);
    let commerce;
    try {
      commerce = parseCommerceFormForCreate(commerceForm);
    } catch (parseError) {
      setError(
        parseError instanceof Error
          ? parseError.message
          : t("userModels3d.invalidPrice"),
      );
      return;
    }
    try {
      const result = await generate({
        variables: {
          name: name.trim(),
          image: file,
          shareWithCommunity: canChoosePrivacy ? shareWithCommunity : true,
          ...commerce,
        },
      });
      const model = result.data?.generateUserModel3DFromImage;
      if (!model?.id) throw new Error("Generation started without an object.");
      onGenerated(model as UserModel3DLibraryItem);
      onClose();
    } catch (submitError) {
      if (getErrorCode(submitError) === "AI_CREDIT_LIMIT_REACHED") {
        setServerOutOfCredits(true);
        return;
      }
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Could not start 3D generation. Try again.",
      );
    }
  };

  const buyCredits = () => {
    const clientUrl = process.env.REACT_APP_CLIENT_URI;
    if (!clientUrl) return;
    window.parent.location.href = `${clientUrl}/mypage/account?buyCredits=1`;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-3 sm:p-6"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget && !generation.loading) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-model-3d-title"
        aria-describedby="create-model-3d-description"
        tabIndex={-1}
        className="relative max-h-[calc(100vh-24px)] w-full max-w-xl overflow-y-auto rounded-2xl bg-[#F8F7F5] p-5 shadow-2xl focus:outline-none sm:p-7"
      >
        <button
          type="button"
          aria-label={t("createModel3d.closeDialog")}
          disabled={generation.loading}
          onClick={onClose}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#111] shadow-sm hover:bg-[#f1efe8] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-50"
        >
          <IoClose size={20} />
        </button>

        <div className="pr-12">
          <h2
            id="create-model-3d-title"
            className="text-xl font-semibold tracking-[-0.02em] text-[#111]"
          >
            {t("createModel3d.title")}
          </h2>
          <p
            id="create-model-3d-description"
            className="mt-1.5 text-sm leading-5 text-[#53514c]"
          >
            {t("createModel3d.description")}
          </p>
        </div>

        <div className="mt-4 space-y-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              chooseImage(event.dataTransfer.files?.[0]);
            }}
            className={`flex w-full items-center justify-center overflow-hidden rounded-xl border border-dashed p-3 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${
              previewUrl ? "min-h-28" : "min-h-36"
            } ${
              dragging
                ? "border-[#111] bg-[#eeece5]"
                : "border-[#b8b5ad] bg-white hover:border-[#77736a]"
            }`}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={t("createModel3d.selectedPreview")}
                className="max-h-32 w-full object-contain"
              />
            ) : (
              <span className="flex flex-col items-center text-[#3d3b37]">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f1efe8]">
                  <FiUploadCloud size={20} />
                </span>
                <span className="mt-2 text-sm font-semibold">
                  {t("createModel3d.dropHint")}
                </span>
                <span className="mt-1 text-xs text-[#625f58]">
                  {t("createModel3d.imageTypesHint")}
                </span>
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            className="hidden"
            aria-label={t("createModel3d.chooseSourceImage")}
            onChange={(event) => chooseImage(event.currentTarget.files?.[0])}
          />

          <label className="block">
            <span className="text-sm font-medium text-[#272622]">
              {t("createModel3d.objectName")}
            </span>
            <input
              type="text"
              value={name}
              maxLength={120}
              disabled={generation.loading}
              onChange={(event) => setName(event.target.value)}
              className="mt-1.5 h-10 w-full rounded-lg border border-[#c9c6be] bg-white px-3 text-sm text-[#111] outline-none placeholder:text-[#6f6b63] focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:opacity-60"
              placeholder={t("createModel3d.nameYourObject")}
            />
          </label>

          <UserModel3DCommerceFields
            value={commerceForm}
            onChange={setCommerceForm}
            disabled={generation.loading}
          />

          {canChoosePrivacy ? (
            <label className="flex cursor-pointer items-start gap-2.5 text-sm leading-5 text-[#3f3d38]">
              <input
                type="checkbox"
                checked={shareWithCommunity}
                disabled={generation.loading}
                onChange={(event) =>
                  setShareWithCommunity(event.target.checked)
                }
                className={`${BONI_CHECKBOX_CLASSNAME} mt-0.5`}
              />
              <span>{t("userModels3d.sharingConsent")}</span>
            </label>
          ) : (
            <p className="text-sm leading-5 text-[#53514c]">
              {t("userModels3d.mandatorySharing")}
            </p>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {error}
            </div>
          )}

          {outOfCredits && (
            <div
              role="status"
              className="rounded-xl bg-[#fff2c2] px-4 py-3 text-sm text-[#342b0a]"
            >
              <div className="font-semibold">
                {t("common.notEnoughAiCredits")}
              </div>
              <p className="mt-1 leading-5">
                {canBuyCredits
                  ? t("createModel3d.buyCreditsHint")
                  : t("createModel3d.contactAdminHint")}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div
              role="note"
              className="inline-flex items-center gap-1.5 self-start rounded-full border border-[#e8dca7] bg-[#fbf8e8] px-2.5 py-1.5 text-[11px] leading-none text-[#3d3b37] sm:self-center"
            >
              <HiSparkles aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              <span>
                {t("createModel3d.creditsNote", {
                  count: GENERATION_CREDIT_COST,
                })}
              </span>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={generation.loading}
                className="h-10 rounded-lg border border-[#bdb9b0] bg-white px-4 text-sm font-semibold text-[#302f2b] hover:border-[#77736a] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              {outOfCredits ? (
                canBuyCredits && (
                  <button
                    type="button"
                    onClick={buyCredits}
                    className="h-10 rounded-lg bg-boniYellowPrimary px-5 text-sm font-semibold text-black hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
                  >
                    {t("common.buyMoreAiCredits")}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  disabled={!file || !name.trim() || generation.loading}
                  onClick={() => void submit()}
                  className="h-10 rounded-lg bg-boniYellowPrimary px-5 text-sm font-semibold text-black hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generation.loading
                    ? t("createModel3d.startingGeneration")
                    : t("createModel3d.generateObject")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
